import type { ReactNode, Ref } from 'react';
import { useState } from 'react';
import { Box, CircularProgress, IconButton, Skeleton, Typography } from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandMore from '@mui/icons-material/ExpandMore';

export interface HierarchyTreeNode {
  /** Stable node id. */
  id: string;
  /** Primary label (person name). */
  primary: ReactNode;
  /** Secondary label (title); rendered after a middot when present. */
  secondary?: ReactNode;
  /**
   * Children of this node:
   *  - an array (including empty `[]`) ⇒ children are already known. A
   *    disclosure chevron shows only when the array is non-empty, and the
   *    child-count badge reads its length once the node is expanded.
   *  - `undefined` ⇒ children are not loaded yet. The node is treated as
   *    lazy: it shows a chevron and {@link HierarchyTreeProps.loadChildren} is
   *    invoked on first expand (an inline skeleton renders while it settles).
   */
  children?: HierarchyTreeNode[];
}

export interface HierarchyTreeProps {
  /** Root-level nodes. */
  nodes: HierarchyTreeNode[];
  /**
   * Loads a lazy node's children on first expand (nodes whose `children` is
   * `undefined`). Omit when every node carries its children inline.
   */
  loadChildren?: (node: HierarchyTreeNode) => Promise<HierarchyTreeNode[]>;
  /** Node ids expanded on first render (their inline children reveal at once). */
  defaultExpandedIds?: readonly string[];
  /** Formats the per-node child-count badge shown once expanded (default `(n)`). */
  formatCount?: (count: number) => string;
  /** Accessible label for the tree root. */
  ariaLabel?: string;
  /** Accessible label for a node's expand/collapse toggle. */
  toggleAriaLabel?: string;
  ref?: Ref<HTMLUListElement>;
}

const INDENT_PX = 22;
const TOGGLE_SLOT = 30;

const listSx = { listStyle: 'none', m: 0, p: 0 } as const;

/**
 * Indented, disclosure-chevron expandable tree composed from a plain list — the
 * kit has no tree primitive, so this fills that gap for the org-hierarchy view.
 *
 * Two loading modes, chosen per node:
 *  - **Inline** — a node ships its `children` array; expanding reveals them
 *    immediately (the full `GET /me/hierarchy` tree renders this way, so a
 *    large subtree stays responsive because collapsed branches aren't in the
 *    DOM until opened).
 *  - **Lazy** — a node's `children` is `undefined`; expanding calls
 *    `loadChildren` and shows an inline branch skeleton until it resolves. This
 *    is the seam a future per-node endpoint uses for the 413 progressive-
 *    disclosure fallback.
 *
 * The child-count badge appears only once a node is expanded and its children
 * are known — a collapsed / not-yet-loaded branch shows a chevron but no count
 * (the API carries no scalar per-node count).
 */
export function HierarchyTree({
  nodes,
  loadChildren,
  defaultExpandedIds,
  formatCount,
  ariaLabel,
  toggleAriaLabel,
  ref,
}: HierarchyTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(defaultExpandedIds ?? []));
  const [loaded, setLoaded] = useState<Map<string, HierarchyTreeNode[]>>(() => new Map());
  const [loading, setLoading] = useState<Set<string>>(() => new Set());

  const childrenOf = (node: HierarchyTreeNode): HierarchyTreeNode[] | undefined =>
    node.children ?? loaded.get(node.id);

  // A lazy node (children undefined, not yet loaded) is assumed expandable; a
  // known node is expandable only when it actually has children.
  const isExpandable = (node: HierarchyTreeNode): boolean =>
    node.children === undefined ? true : node.children.length > 0;

  const toggle = async (node: HierarchyTreeNode): Promise<void> => {
    const { id } = node;
    if (expanded.has(id)) {
      setExpanded((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      return;
    }

    setExpanded((prev) => new Set(prev).add(id));

    // Guard against a double-fire on rapid expand→collapse→expand: skip if a
    // load for this node is already in flight (it isn't in `loaded` yet).
    const needsLoad =
      node.children === undefined && !loaded.has(id) && !loading.has(id) && !!loadChildren;
    if (!needsLoad) return;

    setLoading((prev) => new Set(prev).add(id));
    try {
      const kids = await loadChildren!(node);
      setLoaded((prev) => new Map(prev).set(id, kids));
    } finally {
      setLoading((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const renderNode = (node: HierarchyTreeNode, depth: number): ReactNode => {
    const open = expanded.has(node.id);
    const expandable = isExpandable(node);
    const kids = childrenOf(node);
    const isLoading = loading.has(node.id);
    const count = open && kids !== undefined ? kids.length : undefined;

    return (
      <Box
        component="li"
        key={node.id}
        role="treeitem"
        aria-expanded={expandable ? open : undefined}
        sx={{ m: 0 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pl: `${depth * INDENT_PX + 8}px`,
            pr: 1.5,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {expandable ? (
            <IconButton
              size="small"
              onClick={() => void toggle(node)}
              aria-label={toggleAriaLabel}
              sx={{ width: TOGGLE_SLOT, height: TOGGLE_SLOT, flexShrink: 0 }}
            >
              {open ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: TOGGLE_SLOT, flexShrink: 0 }} />
          )}

          <Typography
            component="span"
            sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}
          >
            {node.primary}
          </Typography>
          {node.secondary != null && node.secondary !== '' && (
            <Typography
              component="span"
              sx={{ fontSize: 13, color: 'text.secondary', minWidth: 0 }}
            >
              · {node.secondary}
            </Typography>
          )}
          {count !== undefined && (
            <Box
              component="span"
              sx={{
                ml: 0.5,
                px: 0.875,
                height: 18,
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: 999,
                bgcolor: 'grey.100',
                border: '1px solid',
                borderColor: 'divider',
                fontSize: 11,
                fontWeight: 700,
                color: 'text.secondary',
                lineHeight: 1,
              }}
            >
              {formatCount ? formatCount(count) : `(${count})`}
            </Box>
          )}
        </Box>

        {open &&
          (isLoading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pl: `${(depth + 1) * INDENT_PX + 8}px`,
                py: 1,
              }}
            >
              <CircularProgress size={14} />
              <Skeleton variant="text" width={180} height={14} />
            </Box>
          ) : kids && kids.length > 0 ? (
            <Box component="ul" role="group" sx={listSx}>
              {kids.map((child) => renderNode(child, depth + 1))}
            </Box>
          ) : null)}
      </Box>
    );
  };

  return (
    <Box component="ul" ref={ref} role="tree" aria-label={ariaLabel} sx={listSx}>
      {nodes.map((node) => renderNode(node, 0))}
    </Box>
  );
}
